import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Phone, Clock, ShieldCheck, MessageCircle } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import Breadcrumbs from '../components/Breadcrumbs';
import EnquiryForm from '../components/EnquiryForm';
import { Button } from '../components/ui/button';
import { PUBLIC_SITE_URL } from '../data/seo';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';
import { enquiryDefaults } from '../data/enquiry';
import { getDentistClinicLD, getContactPageLD, getBreadcrumbLD } from '../data/structuredData';

export default function Enquiry() {
  const [params] = useSearchParams();
  const { condition: defaultCondition, message: defaultMessage } = enquiryDefaults(params);
  const crumbs = [{ name: 'Home', to: '/' }, { name: 'Enquire', to: '/enquiry' }];
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Make an Enquiry — Book a Consultation | Restore Dental Acton"
        description="Send an enquiry to Restore.Dental in Acton, West London. Tell us what you need — Invisalign, implants, whitening, emergency care — and we'll reply within one working day."
        path="/enquiry"
        keywords={['book dentist acton', 'dental consultation acton', 'enquire dentist west london', 'restore dental enquiry']}
        structuredData={[
          getDentistClinicLD(),
          getContactPageLD(),
          getBreadcrumbLD(crumbs.map((c) => ({ name: c.name, url: `${PUBLIC_SITE_URL}${c.to}` }))),
        ]}
      />
      <PageHeader />
      <main className="pt-28 md:pt-32 pb-20">
        <section className="container mx-auto px-4 max-w-6xl">
          <Breadcrumbs items={crumbs} />
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 mt-6 items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-[#006A71] mb-3">Enquire online</p>
              <h1 className="text-4xl sm:text-5xl font-bold text-rabbit leading-tight mb-6" data-testid="enquiry-h1">Tell us how we can help</h1>
              <p className="text-chocolate-malt leading-relaxed mb-8">Fill in the form and {PRACTICE.dentistName}'s team will get back to you within one working day — by email, phone or WhatsApp, whichever you prefer. No obligation, no pressure.</p>
              <ul className="space-y-4 text-sm text-chocolate-malt">
                <li className="flex gap-3"><Clock className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" aria-hidden="true" /><span><strong className="text-rabbit">Reply within one working day.</strong> Emergencies: call us for a same-day slot.</span></li>
                <li className="flex gap-3"><ShieldCheck className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" aria-hidden="true" /><span><strong className="text-rabbit">Free initial consultation</strong> for Invisalign, implants and cosmetic treatment.</span></li>
                <li className="flex gap-3"><MessageCircle className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" aria-hidden="true" /><span><strong className="text-rabbit">Prefer to talk?</strong> Call or WhatsApp {PRACTICE.phoneDisplay}, Mon–Fri 9–6, Sat 9–2.</span></li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button asChild size="lg" variant="outline"><a href={PRACTICE.phoneTel} data-testid="enquiry-call-btn"><Phone className="w-5 h-5 mr-2" />Call {PRACTICE.phoneDisplay}</a></Button>
                <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#20BA5A] text-white"><a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="enquiry-whatsapp-btn">WhatsApp us</a></Button>
              </div>
              <p className="text-xs text-chocolate-malt/70 mt-8">{PRACTICE.addressOneLine} · <Link to="/contact" className="underline underline-offset-4">Directions &amp; opening hours</Link></p>
            </div>
            <EnquiryForm defaultCondition={defaultCondition} defaultMessage={defaultMessage} source="/enquiry" />
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
